import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  Grid,
  Stack,
  Avatar,
  TextField,
  MenuItem,
  Chip,
  Pagination,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import { DataGrid } from '@mui/x-data-grid';
import {
  Visibility,
  Add,
  Info,
  Event,
  Edit,
  Email,
} from "@mui/icons-material";
import MKBox from "../components/MKBox";
import MKTypography from "../components/MKTypography";
import MKButton from "../components/MKButton";
import MKInput from "../components/MKInput";

import CarouselPictureManager from "../components/CarouselPictureManager";
import StripeConnectOnboarding from "../components/StripeConnectOnboarding";
import NewsItemModal from "../components/NewsItemModal";
import EventModal from "../components/EventModal";
import EmailAnnouncementModal from "../components/EmailAnnouncementModal";
import EmailRSVPModal from "../components/EmailRSVPModal";
import MajorDisplay from "../components/MajorDisplay";
import API_BASE_URL from "../apiConfig";
import { PRIMARY_PINK, PRIMARY_PINK_DARK } from "../constants";
import { useTheme } from "../ThemeContext";
import { selectUser } from "../store/userSlice";
import { getDataGridStyles } from "../styles/dataGridStyles";

function EditClubPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const user = useSelector(selectUser);
  
  // Get shared DataGrid styles
  const dataGridStyles = getDataGridStyles(darkMode);
  
  // Define DataGrid columns for events
  const eventColumns = [
    {
      field: 'event',
      headerName: 'Event',
      flex: 1,
      minWidth: 250,
      renderCell: (params) => (
        <Box>
          <Typography variant="subtitle1" fontWeight="bold">
            {params.row.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 200 }}>
            {params.row.description && params.row.description.length > 80 
              ? `${params.row.description.substring(0, 80)}...` 
              : params.row.description}
          </Typography>
        </Box>
      )
    },
    {
      field: 'dateTime',
      headerName: 'Date & Time',
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight="bold">
            {new Date(params.row.start_time).toLocaleDateString()}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {new Date(params.row.start_time).toLocaleTimeString()}
          </Typography>
        </Box>
      )
    },
    {
      field: 'type',
      headerName: 'Type',
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          {params.row.startgg_event && (
            <Chip 
              label="Start.gg" 
              size="small" 
              variant="outlined"
              sx={{ 
                color: PRIMARY_PINK, 
                borderColor: PRIMARY_PINK,
                '& .MuiChip-label': { color: PRIMARY_PINK }
              }}
            />
          )}
          {params.row.rsvp_enabled && (
            <Chip 
              label="RSVP" 
              size="small" 
              variant="outlined"
              sx={{ 
                color: '#4caf50', 
                borderColor: '#4caf50',
                '& .MuiChip-label': { color: '#4caf50' }
              }}
            />
          )}
          {params.row.ticket_available && (
            <Chip 
              label="Tickets" 
              size="small" 
              variant="outlined"
              sx={{ 
                color: '#ff9800', 
                borderColor: '#ff9800',
                '& .MuiChip-label': { color: '#ff9800' }
              }}
            />
          )}
        </Stack>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1.4,
      minWidth: 280,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <MKButton
            variant="outlined"
            size="small"
            onClick={() => handleViewEvent(params.row)}
            sx={{ 
              minWidth: 'auto', 
              px: 1,
              color: darkMode ? '#ffffff' : '#000000',
              borderColor: darkMode ? '#ffffff' : '#000000',
              '&:hover': {
                borderColor: darkMode ? '#ffffff' : '#ffffff',
                backgroundColor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'
              }
            }}
          >
            View
          </MKButton>
          {params.row.rsvp_enabled && (
            <MKButton
              variant="outlined"
              size="small"
              onClick={() => handleEmailRSVPs(params.row)}
              startIcon={<Email />}
              sx={{ 
                minWidth: 'auto', 
                px: 1,
                color: '#4caf50',
                borderColor: '#4caf50',
                '&:hover': {
                  borderColor: '#4caf50',
                  backgroundColor: 'rgba(76, 175, 80, 0.04)'
                }
              }}
            >
              Email RSVPs
            </MKButton>
          )}
          <MKButton
            variant="contained"
            size="small"
            onClick={() => navigate(`/edit-event/${params.row.id}`, { state: { clubId: id } })}
            sx={{ 
              minWidth: 'auto', 
              px: 1,
              bgcolor: PRIMARY_PINK,
              color: '#ffffff',
              "&:hover": {
                bgcolor: PRIMARY_PINK_DARK
              }
            }}
          >
            Edit
          </MKButton>
        </Stack>
      )
    }
  ];
  
  const [club, setClub] = useState(null);
  const [categories, setCategories] = useState([]);
  const [roles, setRoles] = useState([]);
  const [members, setMembers] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [saving, setSaving] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [memberSearch, setMemberSearch] = useState("");
  const [eventsLoading, setEventsLoading] = useState(false);
  
  // Modal states
  const [showNewsModal, setShowNewsModal] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showEmailAnnouncementModal, setShowEmailAnnouncementModal] = useState(false);
  const [showEmailRSVPModal, setShowEmailRSVPModal] = useState(false);
  const [selectedEventForEmail, setSelectedEventForEmail] = useState(null);

  const [form, setForm] = useState({
    name: "",
    handle: "",
    description: "",
    category_id: "",
    avatar_url: "",
    instagram_id: "",
    facebook_url: "",
    discord_url: "",
    youtube_url: "",
    maps_url: "",
    external_link: "",
    show_carousel: false,
    club_mod_feed: false,
  });

  const isClubOwner = club?.owner_id === user?.id;
  
  // Filter members based on search
  const filteredMembers = members.filter(member =>
    member.display_name.toLowerCase().includes(memberSearch.toLowerCase()) ||
    member.email.toLowerCase().includes(memberSearch.toLowerCase())
  );

  useEffect(() => {
    fetchClub();
    fetchCategories();
    fetchRoles();
  }, [id]);

  useEffect(() => {
    if (club?.id) {
      fetchMembers();
      fetchEvents();
    }
  }, [club?.id]);



  const fetchClub = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/clubs/${id}/edit`, {
        credentials: "include",
      });
      if (response.ok) {
        const clubData = await response.json();
        setClub(clubData);
        setForm({
          name: clubData.name || "",
          handle: clubData.handle || "",
          description: clubData.description || "",
          category_id: clubData.category_id || "",
          avatar_url: clubData.avatar_url || "",
          instagram_id: clubData.instagram_id || "",
          facebook_url: clubData.facebook_url || "",
          discord_url: clubData.discord_url || "",
          youtube_url: clubData.youtube_url || "",
          maps_url: clubData.maps_url || "",
          external_link: clubData.external_link || "",
          show_carousel: clubData.show_carousel || false,
          club_mod_feed: clubData.club_mod_feed || false,
        });
      } else {
        setError("Failed to fetch club details");
      }
    } catch (error) {
      console.error("Error fetching club:", error);
      setError("Failed to fetch club details");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/categories`, {
        credentials: "include",
      });
      if (response.ok) {
        const categoriesData = await response.json();
        setCategories(categoriesData);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/roles`, {
        credentials: "include",
      });
      if (response.ok) {
        const rolesData = await response.json();
        setRoles(rolesData);
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
    }
  };

  const fetchMembers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/clubs/${id}/members`, {
        credentials: "include",
      });
      if (response.ok) {
        const membersData = await response.json();
        setMembers(membersData);
      }
    } catch (error) {
      console.error("Error fetching members:", error);
    }
  };

  const fetchEvents = async () => {
    try {
      setEventsLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/clubs/${id}/events`, {
        credentials: "include",
      });
      if (response.ok) {
        const eventsData = await response.json();
        // Filter for upcoming events only
        const upcomingEvents = eventsData.filter(event => new Date(event.end_time) > new Date());
        setEvents(upcomingEvents);
      }
    } catch (error) {
      console.error("Error fetching events:", error);
    } finally {
      setEventsLoading(false);
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUploadProfilePicture = async () => {
    if (!selectedFile) return;
    
    setUploadingPicture(true);
    try {
      const formData = new FormData();
      formData.append("profile_picture", selectedFile);
      
      const response = await fetch(`${API_BASE_URL}/api/clubs/${id}/upload-profile-picture`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      
      if (response.ok) {
        const result = await response.json();
        setForm({ ...form, avatar_url: result.profile_picture_url });
        setClub({ ...club, avatar_url: result.profile_picture_url });
        setSelectedFile(null);
        setSuccess("Profile picture updated successfully!");
      } else {
        setError("Failed to upload profile picture");
      }
    } catch (error) {
      console.error("Error uploading profile picture:", error);
      setError("Failed to upload profile picture");
    } finally {
      setUploadingPicture(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    setSuccess("");
    
    // Frontend validation for required fields
    if (!form.name || form.name.trim() === "") {
      setError("Club name is required");
      setSaving(false);
      return;
    }
    
    // Validate field lengths
    if (form.name.length < 2) {
      setError("Club name must be at least 2 characters long");
      setSaving(false);
      return;
    }
    
    if (form.name.length > 100) {
      setError("Club name must be less than 100 characters");
      setSaving(false);
      return;
    }
    
    
    if (form.description && form.description.length > 500) {
      setError("Description must be less than 500 characters");
      setSaving(false);
      return;
    }
    
    if (form.external_link && form.external_link.length > 500) {
      setError("External link must be less than 500 characters");
      setSaving(false);
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/clubs/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(form),
      });
      
      if (response.ok) {
        const updatedClub = await response.json();
        setClub(updatedClub);
        setSuccess("Club updated successfully!");
      } else {
        const errorData = await response.json();
        setError(errorData.error || "Failed to update club");
      }
    } catch (error) {
      console.error("Error updating club:", error);
      setError("Failed to update club");
    } finally {
      setSaving(false);
    }
  };

  const handleRoleChange = async (userId, newRoleId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/clubs/${id}/members/${userId}/role`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ role_id: newRoleId }),
      });
      
      if (response.ok) {
        fetchMembers(); // Refresh members list
        setSuccess("Member role updated successfully!");
      } else {
        setError("Failed to update member role");
      }
    } catch (error) {
      console.error("Error updating member role:", error);
      setError("Failed to update member role");
    }
  };

  const handleNewsCreated = (newsItem) => {
    setSuccess("News item created successfully!");
    setShowNewsModal(false);
  };

  const handleEventCreated = (event) => {
    setSuccess("Event created successfully!");
    setShowEventModal(false);
    fetchEvents(); // Refresh events list
  };

  const handleViewEvent = (event) => {
    window.open(`/events/${event.id}`, '_blank');
  };

  const handleEmailRSVPs = (event) => {
    setSelectedEventForEmail(event);
    setShowEmailRSVPModal(true);
  };

  if (loading) return <MKBox sx={{ p: { xs: 1, sm: 2, md: 4 }, textAlign: "center" }}><CircularProgress /></MKBox>;
  if (error && !club) return <MKBox sx={{ p: { xs: 1, sm: 2, md: 4 }, textAlign: "center" }}><Alert severity="error">{error}</Alert></MKBox>;

  return (
    <MKBox sx={{ 
      p: { xs: 0, sm: 1, md: 4 },
      overflowX: "hidden",
      width: "100%"
    }}>
      {/* Header */}
      <Box sx={{ mb: { xs: 2, sm: 3, md: 4 } }}>
        <MKTypography variant="h4" fontWeight="bold" mb={2} sx={{ color: PRIMARY_PINK }}>
          Edit Club Dashboard
        </MKTypography>
        <MKTypography variant="body1" sx={{ color: darkMode ? '#ffffff' : '#000000' }}>
          Manage your club settings, events, and content from one central location
        </MKTypography>
      </Box>

      {/* Quick Actions */}
      <Card sx={{ mb: { xs: 2, sm: 3, md: 4 } }}>
        <CardContent>
          <MKTypography variant="h6" fontWeight="bold" mb={2} sx={{ color: PRIMARY_PINK }}>
            Quick Actions
          </MKTypography>
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, flexWrap: 'wrap' }}>
            <MKButton 
              variant="outlined" 
              onClick={() => window.open(`/clubs/${club.handle}`, '_blank')}
              disabled={!club?.handle}
              startIcon={<Visibility />}
              sx={{
                color: darkMode ? '#ffffff' : '#000000',
                borderColor: darkMode ? '#ffffff' : '#000000',
                '&:hover': {
                  borderColor: darkMode ? '#ffffff' : '#000000',
                  backgroundColor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'
                }
              }}
            >
              View in Browser
            </MKButton>
                          <MKButton
                variant="outlined"
                onClick={() => setShowNewsModal(true)}
                disabled={!club?.handle}
                startIcon={<Add />}
                sx={{
                  color: darkMode ? '#ffffff' : '#000000',
                  borderColor: darkMode ? '#ffffff' : '#000000',
                  '&:hover': {
                    borderColor: darkMode ? '#ffffff' : '#000000',
                    backgroundColor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'
                  }
                }}
              >
                Create News Item
              </MKButton>
            <MKButton 
              variant="outlined" 
              onClick={() => setShowEmailAnnouncementModal(true)} 
              disabled={!isClubOwner}
              startIcon={<Info />}
              sx={{
                color: '#ff9800',
                borderColor: '#ff9800',
                '&:hover': {
                  borderColor: '#ff9800',
                  backgroundColor: 'rgba(255, 152, 0, 0.04)'
                }
              }}
            >
              Email Announcement
            </MKButton>
            <MKButton 
              variant="contained" 
              onClick={() => setShowEventModal(true)} 
              disabled={!club?.handle}
              startIcon={<Event />}
              sx={{
                bgcolor: PRIMARY_PINK,
                color: '#ffffff',
                "&:hover": {
                  bgcolor: PRIMARY_PINK_DARK
                }
              }}
            >
              Create New Event
            </MKButton>
          </Box>
        </CardContent>
      </Card>

      {/* Dashboard Grid Layout */}
      <Grid container spacing={3}>
        {/* Left Column - Club Settings */}
        <Grid item xs={12} lg={6}>
          {/* Combined Club Settings Section */}
          <Card sx={{ mb: { xs: 2, sm: 2, md: 3 }, height: 'fit-content' }}>
            <CardContent>
              {/* Club Information */}
              <MKTypography variant="h6" fontWeight="bold" mb={3} sx={{ color: PRIMARY_PINK }}>
                Club Information
              </MKTypography>
              <Stack spacing={2} sx={{ mb: { xs: 2, sm: 3, md: 4 } }}>
                <MKInput label="Name" name="name" value={form.name || ""} onChange={handleChange} fullWidth required />
                <Box sx={{ mb: 2 }}>
                  <MKTypography variant="body2" sx={{ 
                    color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                    mb: 1,
                    fontWeight: 500
                  }}>
                    Handle
                  </MKTypography>
                  <Box sx={{
                    p: 1,
                    mb: 1,
                    border: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)'}`,
                    borderRadius: 1,
                    backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                    color: darkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
                    fontFamily: 'monospace',
                    fontSize: '0.875rem'
                  }}>
                    @{form.handle || 'No handle set'}
                  </Box>
                </Box>
                <MKInput label="Description" name="description" value={form.description || ""} onChange={handleChange} fullWidth multiline rows={3} />
                
                {/* Category Selection */}
                <FormControl fullWidth>
                  <InputLabel 
                    id="category-select-label"
                    sx={{ color: darkMode ? '#ffffff' : '#000000' }}
                  >
                    Category
                  </InputLabel>
                  <Select
                    labelId="category-select-label"
                    name="category_id"
                    value={form.category_id || ""}
                    label="Category"
                    onChange={handleChange}
                    sx={{ 
                      fontSize: 18, 
                      height: 56,
                      '& .MuiSelect-select': {
                        color: darkMode ? '#ffffff' : '#000000',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: darkMode ? '#ffffff' : '#000000',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: darkMode ? '#ffffff' : '#000000',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: darkMode ? '#ffffff' : '#000000',
                      },
                    }}
                  >
                    <MenuItem value=""><em>Select a category</em></MenuItem>
                    {categories.map(category => (
                      <MenuItem key={category.id} value={category.id}>{category.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>

              {/* Profile Picture Section */}
              <MKTypography variant="h6" fontWeight="bold" mb={3} sx={{ color: PRIMARY_PINK }}>
                Profile Picture
              </MKTypography>
              <Stack direction="row" spacing={2} alignItems="center" mb={2} sx={{ mb: { xs: 2, sm: 3, md: 4 } }}>
                <Avatar 
                  src={selectedFile ? URL.createObjectURL(selectedFile) : form.avatar_url} 
                  sx={{ 
                    width: 80, 
                    height: 80,
                    '& img': {
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: '50%'
                    }
                  }}
                  alt={club?.name}
                >
                  {club?.name?.charAt(0)?.toUpperCase() || 'C'}
                </Avatar>
                <MKBox>
                  <input accept="image/*" style={{ display: 'none' }} id="profile-picture-upload" type="file" onChange={handleFileSelect} />
                  <label htmlFor="profile-picture-upload">
                    <MKButton 
                      variant="outlined" 
                      component="span"
                      sx={{
                        color: darkMode ? '#ffffff' : '#000000',
                        borderColor: darkMode ? '#ffffff' : '#000000',
                        '&:hover': {
                          borderColor: darkMode ? '#ffffff' : '#000000',
                          backgroundColor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'
                        }
                      }}
                    >
                      Choose File
                    </MKButton>
                  </label>
                  {selectedFile && (
                    <>
                      <MKButton variant="contained" onClick={handleUploadProfilePicture} disabled={uploadingPicture} sx={{ ml: 1 }}>
                        {uploadingPicture ? "Uploading..." : "Upload"}
                      </MKButton>
                    </>
                  )}
                </MKBox>
              </Stack>
              {selectedFile && (
                <MKTypography variant="body2" color="text.secondary" sx={{ mb: { xs: 2, sm: 3, md: 4 } }}>Selected: {selectedFile.name}</MKTypography>
              )}
              
              {/* Social Links */}
              <MKTypography variant="h6" fontWeight="bold" mb={3} sx={{ color: PRIMARY_PINK }}>
                Social Links
              </MKTypography>
              <Stack spacing={2} sx={{ mb: { xs: 2, sm: 3, md: 4 } }}>
                <MKInput label="Instagram ID" name="instagram_id" value={form.instagram_id || ""} onChange={handleChange} fullWidth />
                <MKInput label="Facebook URL" name="facebook_url" value={form.facebook_url || ""} onChange={handleChange} fullWidth />
                <MKInput label="Discord URL" name="discord_url" value={form.discord_url || ""} onChange={handleChange} fullWidth />
                <MKInput label="YouTube URL" name="youtube_url" value={form.youtube_url || ""} onChange={handleChange} fullWidth />
                <MKInput label="Maps URL" name="maps_url" value={form.maps_url || ""} onChange={handleChange} fullWidth />
                <MKInput label="External Link" name="external_link" value={form.external_link || ""} onChange={handleChange} fullWidth />
              </Stack>
              
              {/* Club Settings */}
              <MKTypography variant="h6" fontWeight="bold" mb={3} sx={{ color: PRIMARY_PINK }}>
                Club Settings
              </MKTypography>
              <Stack spacing={2} sx={{ mb: { xs: 2, sm: 3, md: 4 } }}>
                                 <FormControl fullWidth>
                   <InputLabel 
                     id="show-carousel-select-label"
                     sx={{ color: darkMode ? '#ffffff' : '#000000' }}
                   >
                     Show Carousel
                   </InputLabel>
                   <Select
                     labelId="show-carousel-select-label"
                     name="show_carousel"
                     value={form.show_carousel ? "true" : "false"}
                     label="Show Carousel"
                     onChange={e => setForm({ ...form, show_carousel: e.target.value === "true" })}
                     sx={{ 
                       fontSize: 18, 
                       height: 56,
                       '& .MuiSelect-select': {
                         color: darkMode ? '#ffffff' : '#000000',
                       },
                       '& .MuiOutlinedInput-notchedOutline': {
                         borderColor: darkMode ? '#ffffff' : '#000000',
                       },
                       '&:hover .MuiOutlinedInput-notchedOutline': {
                         borderColor: darkMode ? '#ffffff' : '#000000',
                       },
                       '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                         borderColor: darkMode ? '#ffffff' : '#000000',
                       },
                     }}
                   >
                     <MenuItem value="false">No</MenuItem>
                     <MenuItem value="true">Yes</MenuItem>
                   </Select>
                 </FormControl>
                                 <FormControl fullWidth>
                   <InputLabel 
                     id="club-mod-feed-select-label"
                     sx={{ color: darkMode ? '#ffffff' : '#000000' }}
                   >
                     Club Mod Feed
                   </InputLabel>
                   <Select
                     labelId="club-mod-feed-select-label"
                     name="club_mod_feed"
                     value={form.club_mod_feed ? "true" : "false"}
                     label="Club Mod Feed"
                     onChange={e => setForm({ ...form, club_mod_feed: e.target.value === "true" })}
                     sx={{ 
                       fontSize: 18, 
                       height: 56,
                       '& .MuiSelect-select': {
                         color: darkMode ? '#ffffff' : '#000000',
                       },
                       '& .MuiOutlinedInput-notchedOutline': {
                         borderColor: darkMode ? '#ffffff' : '#000000',
                       },
                       '&:hover .MuiOutlinedInput-notchedOutline': {
                         borderColor: darkMode ? '#ffffff' : '#000000',
                       },
                       '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                         borderColor: darkMode ? '#ffffff' : '#000000',
                       },
                     }}
                   >
                     <MenuItem value="false">No</MenuItem>
                     <MenuItem value="true">Yes</MenuItem>
                   </Select>
                 </FormControl>
              </Stack>

                             {/* Save Button */}
               {success && !success.includes('Member') && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
               {error && !error.includes('Member') && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              <MKButton 
                variant="contained" 
                onClick={handleSave} 
                disabled={saving} 
                fullWidth
                sx={{ 
                  minWidth: 120,
                  bgcolor: PRIMARY_PINK,
                  color: '#ffffff',
                  "&:hover": {
                    bgcolor: PRIMARY_PINK_DARK
                  }
                }}
              >
                {saving ? "Saving..." : "Save Changes"}
              </MKButton>
            </CardContent>
          </Card>

          {/* Member Management - Only for Club Owners */}
          {isClubOwner && (
            <Card sx={{ mt: 3, height: 'fit-content' }}>
              <CardContent>
                <MKTypography variant="h6" fontWeight="bold" mb={3} sx={{ color: PRIMARY_PINK }}>
                  👥 Member Management
                </MKTypography>
                
                {/* Member Success/Error Messages */}
                {success && success.includes('Member') && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
                {error && error.includes('Member') && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                
                {/* Member Search */}
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Search members by name or email..."
                  value={memberSearch}
                  onChange={e => setMemberSearch(e.target.value)}
                  sx={{ mb: { xs: 2, sm: 2, md: 3 } }}
                />
                
                {/* Members List */}
                {members.length === 0 ? (
                  <MKTypography color="text.secondary">No members found.</MKTypography>
                ) : (
                  <>
                    <Stack spacing={2}>
                      {filteredMembers.map((member) => (
                        <Card key={member.user_id} sx={{ p: { xs: 1, sm: 1.5, md: 2 } }}>
                                                     <Grid container alignItems="center" spacing={2}>
                             <Grid item xs={8}>
                               <Stack direction="row" spacing={2} alignItems="center">
                                 <Avatar 
                                   src={member.avatar_url} 
                                   alt={member.display_name}
                                   sx={{
                                     '& img': {
                                       width: '100%',
                                       height: '100%',
                                       objectFit: 'cover',
                                       borderRadius: '50%'
                                     }
                                   }}
                                 />
                                 <MKBox>
                                   <MKTypography variant="subtitle1" fontWeight="bold" sx={{ color: darkMode ? '#ffffff' : '#000000' }}>
                                     {member.display_name}
                                   </MKTypography>
                                   <MKTypography variant="body2" sx={{ color: darkMode ? '#ffffff' : '#000000' }}>
                                     {member.email}
                                   </MKTypography>
                                   <MKTypography variant="body2" sx={{ color: darkMode ? '#ffffff' : '#000000' }}>
                                     Current Role: {member.role_name || 'Member'}
                                   </MKTypography>
                                 </MKBox>
                               </Stack>
                             </Grid>
                             <Grid item xs={4} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                               {/* Only show select for non-owner */}
                               {member.role_name && member.role_name.toLowerCase() === 'club owner' ? (
                                 <MKTypography variant="body2" sx={{ 
                                   minWidth: 150, 
                                   textAlign: 'center', 
                                   alignSelf: 'center', 
                                   color: darkMode ? '#ffffff' : '#000000' 
                                 }}>
                                   Club Owner
                                 </MKTypography>
                               ) : (
                                 <FormControl sx={{ minWidth: 200, maxWidth: 250 }}>
                                   <InputLabel 
                                     id={`role-select-label-${member.user_id}`}
                                     sx={{ color: darkMode ? '#ffffff' : '#000000' }}
                                   >
                                     Role
                                   </InputLabel>
                                   <Select
                                     labelId={`role-select-label-${member.user_id}`}
                                     value={member.role_id || ''}
                                     label="Role"
                                     onChange={(e) => handleRoleChange(member.user_id, e.target.value)}
                                     sx={{
                                       fontSize: 18,
                                       height: 56,
                                       '& .MuiSelect-select': {
                                         color: darkMode ? '#ffffff' : '#000000',
                                       },
                                       '& .MuiOutlinedInput-notchedOutline': {
                                         borderColor: darkMode ? '#ffffff' : '#000000',
                                       },
                                       '&:hover .MuiOutlinedInput-notchedOutline': {
                                         borderColor: darkMode ? '#ffffff' : '#000000',
                                       },
                                       '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                         borderColor: darkMode ? '#ffffff' : '#000000',
                                       },
                                     }}
                                     MenuProps={{
                                       PaperProps: {
                                         sx: {
                                           fontSize: 18,
                                           minHeight: 48,
                                         }
                                       }
                                     }}
                                   >
                                     {roles.map(role => (
                                       <MenuItem key={role.id} value={role.id} sx={{ fontSize: 18, minHeight: 48 }}>
                                         {role.name}
                                       </MenuItem>
                                     ))}
                                   </Select>
                                 </FormControl>
                               )}
                             </Grid>
                           </Grid>
                        </Card>
                      ))}
                    </Stack>
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </Grid>
        
        {/* Right Column - Content Management */}
        <Grid item xs={12} lg={6}>
          {/* Carousel Picture Management */}
          {form.show_carousel && (
            <Card sx={{ mb: { xs: 2, sm: 2, md: 3 }, height: 'fit-content' }}>
              <CardContent>
                <CarouselPictureManager clubId={id} clubHandle={club?.handle || ""} />
              </CardContent>
            </Card>
          )}
          
          {/* Events Management */}
          <Card sx={{ mb: { xs: 2, sm: 2, md: 3 }, height: 'fit-content' }}>
            <CardContent>
              <MKTypography variant="h6" fontWeight="bold" mb={3} sx={{ color: PRIMARY_PINK }}>
                <Event sx={{ mr: 1, verticalAlign: 'middle' }} />
                Upcoming Events
              </MKTypography>
              
              {eventsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : events.length === 0 ? (
                <MKTypography color="text.secondary" sx={{ textAlign: 'center', p: 3 }}>
                  No upcoming events found.
                </MKTypography>
              ) : (
                <Box sx={{ width: '100%', ...dataGridStyles }}>
                  <DataGrid
                    rows={events}
                    columns={eventColumns}
                    getRowId={(row) => row.id}
                    loading={eventsLoading}
                    pageSize={5}
                    rowsPerPageOptions={[5, 10, 25]}
                    disableSelectionOnClick
                    hideFooterPagination={events.length <= 5}
                    sx={{
                      ...dataGridStyles,
                      minHeight: 400,
                      '& .MuiDataGrid-main': {
                        minHeight: 400,
                      },
                      '& .MuiDataGrid-virtualScroller': {
                        minHeight: 400,
                      },
                      '& .MuiDataGrid-row': {
                        '&:hover': {
                          backgroundColor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                        },
                      },
                      '& .MuiDataGrid-cell': {
                        display: 'flex',
                        alignItems: 'center',
                        padding: '8px 16px',
                      },
                    }}
                    componentsProps={{
                      filterPanel: {
                        sx: {
                          backgroundColor: darkMode ? '#1e1e1e' : '#ffffff',
                          color: darkMode ? '#ffffff' : '#000000',
                        },
                      },
                    }}
                  />
                </Box>
              )}
            </CardContent>
          </Card>
          
          {/* Major Events Management */}
          <Card sx={{ mb: { xs: 2, sm: 2, md: 3 }, height: 'fit-content' }}>
            <CardContent>
              <MKTypography variant="h6" fontWeight="bold" mb={3} sx={{ color: PRIMARY_PINK }}>
                <Event sx={{ mr: 1, verticalAlign: 'middle' }} />
                Major Events
              </MKTypography>
              
              <MKTypography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Create and manage major events that group multiple tournaments together.
              </MKTypography>
              
              <MajorDisplay 
                clubId={id} 
                clubName={club?.name || ""} 
                isOwner={isClubOwner}
              />
            </CardContent>
          </Card>
          
          {/* Stripe Connect Onboarding - Only for Club Owners */}
          {/* TODO: Re-enable payment setup later
          {isClubOwner && (
            <Card sx={{ mb: { xs: 2, sm: 2, md: 3 }, height: 'fit-content' }}>
              <CardContent>
                <MKTypography variant="h6" fontWeight="bold" mb={3} sx={{ color: PRIMARY_PINK }}>
                  💳 Payment Setup
                </MKTypography>
                <MKTypography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Club owners can set up payment accounts to receive ticket sales directly
                </MKTypography>
                <StripeConnectOnboarding
                  clubId={id}
                  clubHandle={club?.handle || ""}
                />
              </CardContent>
            </Card>
          )}
          */}


        </Grid>
      </Grid>

      {/* Modals */}
      <NewsItemModal
        open={showNewsModal}
        onClose={() => setShowNewsModal(false)}
        onNewsCreated={handleNewsCreated}
        clubId={id}
        clubName={club?.name || ""}
      />

      <EventModal
        open={showEventModal}
        onClose={() => setShowEventModal(false)}
        clubId={id}
        clubName={club?.name || ""}
        onEventCreated={handleEventCreated}
      />

      <EmailAnnouncementModal
        open={showEmailAnnouncementModal}
        onClose={() => setShowEmailAnnouncementModal(false)}
        clubId={id}
        clubName={club?.name || ""}
      />

      <EmailRSVPModal
        open={showEmailRSVPModal}
        onClose={() => setShowEmailRSVPModal(false)}
        clubId={id}
        clubName={club?.name || ""}
        event={selectedEventForEmail}
      />
    </MKBox>
  );
}

export default EditClubPage;